import { consola } from 'consola'
import { Capacitor } from '@capacitor/core'
import type { SelectedDeliveryAddress } from '~/utils/types/user'

/**
 * Композабл для универсального хранения выбранного адреса доставки
 * Работает в браузере (localStorage) и мобильных приложениях (SecureStorage)
 *
 * Вместе с адресом храним его координаты: без них заказ на доставку не оформить
 * (по ним бэкенд подбирает филиал), а держать их в непереживающем перезапуск
 * сторе — ровно тот баг, из-за которого адрес был виден, а заказать было нельзя.
 */
/**
 * Версия записи. 2 — координаты в запись попадают только вместе с домом и никогда не
 * равны точке филиала (после #8300). Записи без версии сделаны старым клиентом: там
 * могла лежать строка хутора с координатами филиала, на котором открылась карта, —
 * при восстановлении такие координаты отбрасываются (useUserProfileStore).
 */
export const DELIVERY_ADDRESS_RECORD_VERSION = 2

export const useDeliveryAddressStorage = () => {
  const STORAGE_KEY = 'selected_delivery_address'

  /**
   * Сохранить адрес доставки
   */
  const setDeliveryAddress = async (address: SelectedDeliveryAddress | null) => {
    try {
      const serializedAddress = address
        ? JSON.stringify({ ...address, v: DELIVERY_ADDRESS_RECORD_VERSION })
        : null

      if (process.client && Capacitor.isNativePlatform()) {
        // Мобильное приложение - используем SecureStorage
        const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
        
        if (serializedAddress) {
          await SecureStoragePlugin.set({ 
            key: STORAGE_KEY, 
            value: serializedAddress, 
          })
          consola.debug('✅ Адрес доставки сохранен в SecureStorage (мобильное приложение)')
        } else {
          await SecureStoragePlugin.remove({ key: STORAGE_KEY })
          consola.debug('✅ Адрес доставки удален из SecureStorage (мобильное приложение)')
        }
      } else if (process.client) {
        // Браузер - используем localStorage
        if (serializedAddress) {
          localStorage.setItem(STORAGE_KEY, serializedAddress)
          consola.debug('✅ Адрес доставки сохранен в localStorage (браузер)')
        } else {
          localStorage.removeItem(STORAGE_KEY)
          consola.debug('✅ Адрес доставки удален из localStorage (браузер)')
        }
      }
    } catch (error) {
      consola.error('❌ Ошибка сохранения адреса доставки:', error)
    }
  }

  /**
   * Получить сохраненный адрес доставки
   */
  const getDeliveryAddress = async (): Promise<SelectedDeliveryAddress | null> => {
    try {
      let serializedAddress: string | null = null

      if (process.client && Capacitor.isNativePlatform()) {
        // Мобильное приложение - используем SecureStorage
        const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
        
        try {
          const result = await SecureStoragePlugin.get({ key: STORAGE_KEY })
          serializedAddress = result?.value || null
          consola.debug('📱 Адрес доставки загружен из SecureStorage (мобильное приложение):', serializedAddress)
        } catch {
          // Ключ не найден в SecureStorage - это нормально при первом запуске
          consola.debug('📱 Адрес доставки не найден в SecureStorage (мобильное приложение) - используем значение по умолчанию')
          return null
        }
      } else if (process.client) {
        // Браузер - используем localStorage
        serializedAddress = localStorage.getItem(STORAGE_KEY)
        consola.debug('🌐 Адрес доставки загружен из localStorage (браузер):', serializedAddress)
      }

      if (serializedAddress) {
        const parsed = JSON.parse(serializedAddress)
        if (parsed && typeof parsed === 'object' && parsed.address) {
          return parsed
        }
      }

      return null
    } catch (error) {
      consola.error('❌ Ошибка загрузки адреса доставки:', error)
      return null
    }
  }

  /**
   * Удалить сохраненный адрес доставки
   */
  const removeDeliveryAddress = async () => {
    await setDeliveryAddress(null)
  }

  /**
   * Проверить поддержку хранилища
   */
  const isStorageSupported = (): boolean => {
    if (!process.client) return false
    
    if (Capacitor.isNativePlatform()) {
      return true // SecureStorage всегда доступен в Capacitor
    }
    
    // Проверяем поддержку localStorage
    try {
      const test = 'storage_test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch {
      return false
    }
  }

  /**
   * Получить тип используемого хранилища
   */
  const getStorageType = (): 'secure_storage' | 'local_storage' | 'none' => {
    if (!process.client) return 'none'
    
    if (Capacitor.isNativePlatform()) {
      return 'secure_storage'
    }
    
    return isStorageSupported() ? 'local_storage' : 'none'
  }

  return {
    setDeliveryAddress,
    getDeliveryAddress,
    removeDeliveryAddress,
    isStorageSupported,
    getStorageType,
  }
}
